import { Dispatch, ReactNode, SetStateAction, useMemo } from "react";
import { Collapse } from "reactstrap";
import classNames from "classnames";

import RecordActionsMenu from "../../../Drawers/Records/RecordActionsMenu";
import Draggable, { DragHandleProps } from "../../../Dnd/Draggable";
import { ReusableViewIDs } from "../../../../types/PageInterfaces";
import SectionRenderer from "../../../Records/SectionRenderer";
import RecordNameCell from "../../../Records/RecordNameCell";
import DotsOptionsMenu from "../../../Menus/DotsOptionsMenu";
import { Module } from "../../../../types/TenantInterfaces";
import { DetailView } from "../SharedToolbar/SharedToolbar";

interface Props {
  record: any;
  imageField: any;
  imageFieldPresentation: any;
  currentModule: Module;
  setOpenRecordData: Dispatch<SetStateAction<DetailView>>;
  metadataField: any;
  metadataFieldType: any;
  setUpdateRecordID: Dispatch<SetStateAction<string | null>>;
  fieldsSection: {
    Fields: {
      Field: any;
      ColSpan: number;
    }[];
    Title: string;
  };
  isSelected?: boolean;
  onSelect?: () => void;
  maxHeight?: number;
  draggable?: boolean;
  startDroppableID?: string;
  shrink?: boolean;
  viewID: string;
  reusableViews?: ReusableViewIDs;
  commentsWidget?: boolean;
}

export const AppCardWrapper = ({
  dragHandleProps,
  isSelected,
  onSelect,
  record,
  currentModule,
  setOpenRecordData,
  imageField,
  imageFieldPresentation,
  metadataField,
  metadataFieldType,
  setUpdateRecordID,
  maxHeight,
  renderedSection,
  isDragging,
  shrink,
  viewID,
  draggable,
  reusableViews,
  commentsWidget = true,
}: Omit<Props, "fieldsSection"> & {
  dragHandleProps?: DragHandleProps;
  renderedSection: ReactNode;
  isDragging?: boolean;
}) => (
  <>
    <div
      className={classNames(
        "w-100 border-bottom py-2 pr-2 d-flex justify-content-between",
        { "bg-primary-selected rounded-top": isSelected }
      )}
    >
      <div className="tw:flex tw:w-full">
        {dragHandleProps && (
          <i
            className="mdi mdi-drag text-lg px-2"
            {...dragHandleProps?.attributes}
            {...dragHandleProps?.listeners}
            style={{
              cursor: isDragging ? "grabbing" : "grab",
              touchAction: "none",
            }}
          />
        )}
        {!currentModule?.Dynamic && onSelect ? (
          <div
            className={`cursor-pointer pt-1 ${
              dragHandleProps ? "pr-2" : "px-2"
            }`}
            onClick={onSelect}
          >
            <input
              className="cursor-pointer"
              type="checkbox"
              checked={isSelected}
              readOnly
            />
          </div>
        ) : (
          <div className="pr-2"></div>
        )}
        <div
          className="tw:flex tw:w-full tw:mr-1"
          style={{ contain: "layout" }}
        >
          <div className="tw:w-full">
            <RecordNameCell
              record={record}
              onOpen={() => {
                if (currentModule?.Dynamic) return;

                setOpenRecordData({
                  recordID: record?._id || record?.id,
                  moduleAPIName: currentModule?.APIName,
                  tab: reusableViews?.RecordDetail
                    ? `Reuse-${reusableViews?.RecordDetail}-Summary`
                    : "Summary",
                  viewID: viewID,
                });
              }}
              imageField={imageField}
              imageFieldPresentation={imageFieldPresentation}
              metadataField={metadataField}
              metadataFieldType={metadataFieldType}
              maxWidth={
                !!record?._Synced && draggable ? (shrink ? 200 : 230) : 250
              }
              fontSize={13}
              // @ts-ignore
              tooltipPlacement={"autoVertical"}
              wrap={shrink}
              className="pt-1"
              commentsWidget={commentsWidget}
              nameField={
                currentModule?.Templates?.RecordListView?.RecordNameField
              }
            />
          </div>
        </div>
      </div>
      {!currentModule?.Dynamic && (
        <DotsOptionsMenu
          options={
            // @ts-ignore
            <RecordActionsMenu
              record={record}
              toggleUpdateRecordDrawer={() => setUpdateRecordID(record._id)}
              module={currentModule}
            />
          }
          dotsSize={20}
          placement="autoVerticalEnd"
        />
      )}
    </div>
    <Collapse isOpen={!shrink}>
      <div
        className="p-2"
        style={{
          maxHeight: maxHeight,
          overflowY: "auto",
          overflowX: "hidden",
        }}
      >
        {renderedSection}
      </div>
    </Collapse>
  </>
);

const CardRecord = ({
  record,
  currentModule,
  fieldsSection,
  draggable = false,
  startDroppableID,
  ...props
}: Props) => {
  // Memoized rendered section due to laggy selection
  const renderedSection = useMemo(
    () => (
      <SectionRenderer
        section={fieldsSection}
        record={record}
        module={currentModule}
        marginBottom={false}
        isCard={true}
        reusableViewReferenceFields={props.reusableViews?.ReferenceFields}
      />
    ),
    [fieldsSection, record, currentModule]
  );

  if (draggable) {
    return (
      <Draggable
        id={record?._id}
        childComponent={(dragState, dragProps) => (
          <div className="btn-oauth rounded bg-white">
            <AppCardWrapper
              record={record}
              currentModule={currentModule}
              dragHandleProps={dragProps}
              renderedSection={renderedSection}
              isDragging={dragState?.isDragging}
              draggable={draggable}
              {...props}
            />
          </div>
        )}
        dragOnly
        data={{ record, startDroppableID }}
        useDragHandle
      />
    );
  }

  // For when drag is disabled, but is still inside a board.
  if (!draggable && startDroppableID) {
    return (
      <div className="btn-oauth rounded bg-white">
        <AppCardWrapper
          record={record}
          currentModule={currentModule}
          renderedSection={renderedSection}
          draggable={draggable}
          {...props}
        />
      </div>
    );
  }

  return (
    <CardContainer>
      <AppCardWrapper
        record={record}
        currentModule={currentModule}
        renderedSection={renderedSection}
        draggable={draggable}
        {...props}
      />
    </CardContainer>
  );
};

export const CardContainer = (props: any) => (
  <div
    className="border btn-oauth rounded bg-white"
    style={{ minWidth: 320, width: "100%", maxWidth: 350 }}
  >
    {props.children}
  </div>
);

export default CardRecord;
